/**
 * Slice 3 — First-Campaign Concierge.
 *
 * Guided 3-step flow shown INSTEAD of the empty campaigns tile when
 * the tenant has zero campaigns. Steps:
 *   1. Objective (visual cards)
 *   2. Region + daily budget
 *   3. Confirm summary → POST /campaigns
 *
 * Step 3's strategy summary is a deterministic template (LLM concierge
 * lands in Slice 4).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Target, Users, Rocket, Repeat, Megaphone, Calendar,
  Sparkles, GraduationCap, ChevronLeft, ChevronRight, Loader2,
  CheckCircle2,
} from "lucide-react";
import { api } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { TemplateFallbackBanner } from "./TemplateFallbackBanner";

const OBJECTIVES = [
  { key: "lead", Icon: Users,
    title: "Lead", desc: "Get more inquiries from potential sellers/buyers." },
  { key: "reach", Icon: Target,
    title: "Reach", desc: "Maximise ad views across the target audience." },
  { key: "retargeting", Icon: Repeat,
    title: "Retargeting", desc: "Re-engage people who visited a listing." },
  { key: "branding", Icon: Megaphone,
    title: "Branding", desc: "Build brand awareness and recall." },
  { key: "webinar", Icon: GraduationCap,
    title: "Webinar", desc: "Drive registrations to an event or webinar." },
  { key: "appointment_booking", Icon: Calendar,
    title: "Appointment", desc: "Get people to book a viewing / call." },
  { key: "launch", Icon: Rocket,
    title: "Launch", desc: "Promote a new listing or product launch." },
  { key: "recruiting", Icon: Sparkles,
    title: "Recruiting", desc: "Attract new agents / staff." },
];

export default function FirstCampaignConcierge() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [regions, setRegions] = useState([]);
  const [state, setState] = useState({
    campaign_type: null,
    region_id: null,
    daily_budget_eur: 50,
  });
  // Slice 4b-3 — Step 3 LLM brief state.
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefError, setBriefError] = useState(null);
  const [sessionCorrId] = useState(
    () => `concierge-${Math.random().toString(36).slice(2, 12)}`);
  // Wave 1 — Marketing Plan (AARRR) via coreyhaines skill.
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState(null);

  useEffect(() => {
    api.get("/tenancy/regions", { skipPermissionToast: true })
      .then((r) => setRegions(r.data?.items || []))
      .catch(() => setRegions([]));
  }, []);

  function setField(k, v) { setState((s) => ({ ...s, [k]: v })); }

  const objMeta = OBJECTIVES.find((o) => o.key === state.campaign_type);

  /* Slice 4b-3 — Fetch (or regenerate) the LLM brief for Step 3.
   * Backend contract: POST /concierge/step3 → { source, fallback_reason,
   * regenerations_used, regenerations_remaining, ...brief }.
   * Frontend never surfaces 5xx — the endpoint always returns 200 with
   * a template-fallback envelope on failure. */
  async function fetchBrief() {
    setBriefLoading(true);
    setBriefError(null);
    try {
      const region = regions.find((r) => r.id === state.region_id);
      const body = {
        industry: "real_estate",
        goal: `First campaign — ${objMeta?.title || "Campaign"}`,
        target_audience: null,
        region: region?.name || null,
        campaign_type: state.campaign_type,
        daily_budget_eur: Number(state.daily_budget_eur),
        locale: "de",
        session_correlation_id: sessionCorrId,
      };
      const r = await api.post("/concierge/step3", body);
      setBrief(r.data);
    } catch (e) {
      setBriefError(String(
        e?.response?.data?.detail?.error || e?.message || e));
    } finally {
      setBriefLoading(false);
    }
  }

  // Auto-fetch when the user first lands on Step 3.
  useEffect(() => {
    if (step === 3 && !brief && !briefLoading) {
      fetchBrief();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Wave 1 — POST /concierge/marketing-plan → AARRR envelope.
  async function fetchMarketingPlan() {
    setPlanLoading(true);
    setPlanError(null);
    try {
      const region = regions.find((r) => r.id === state.region_id);
      const body = {
        goal: `First campaign — ${objMeta?.title || "Campaign"}`,
        industry: "real_estate",
        target_audience: null,
        region: region?.name || null,
        campaign_type: state.campaign_type,
        daily_budget_eur: Number(state.daily_budget_eur),
        locale: "de",
        session_key: sessionCorrId,
      };
      const r = await api.post("/concierge/marketing-plan", body);
      setPlan(r.data);
    } catch (e) {
      setPlanError(String(
        e?.response?.data?.detail?.error || e?.message || e));
    } finally {
      setPlanLoading(false);
    }
  }

  async function submit() {
    setBusy(true);
    try {
      const payload = {
        name: `First campaign — ${objMeta?.title || "Campaign"}`,
        campaign_type: state.campaign_type,
        platforms: ["meta"],
        budget: {
          amount: Number(state.daily_budget_eur),
          currency: "EUR", type: "daily", pacing: "standard",
        },
        region_ids: state.region_id ? [state.region_id] : [],
        tracking: {
          utm_source: "meta", utm_medium: "cpc",
          utm_campaign: "first_campaign_concierge",
          conversion_event: "lead",
        },
      };
      const r = await api.post("/campaigns", payload);
      toast.success("First campaign created!");
      nav(`/app/campaigns/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error
        || "Failed to create campaign");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="border-2 border-dashed" data-testid="first-campaign-concierge">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="size-5 text-amber-500" />
          Create your first campaign in 3 steps
        </CardTitle>
        <div className="flex items-center gap-1 pt-2">
          {[1, 2, 3].map((n) => (
            <div key={n}
                    className={`h-1 flex-1 rounded ${
                      n <= step ? "bg-amber-500" : "bg-muted"}`}
                    data-testid={`concierge-step-indicator-${n}`} />
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {step === 1 && (
          <div data-testid="concierge-step-1">
            <p className="text-sm text-muted-foreground mb-3">
              What's the goal of your first campaign?
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {OBJECTIVES.map((o) => {
                const Icon = o.Icon;
                const active = state.campaign_type === o.key;
                return (
                  <button
                    key={o.key}
                    onClick={() => setField("campaign_type", o.key)}
                    data-testid={`concierge-objective-${o.key}`}
                    className={`p-3 border rounded-lg text-left space-y-1 hover:bg-muted/40 ${
                      active ? "border-amber-500 bg-amber-50 dark:bg-amber-950" : ""}`}
                  >
                    <Icon className="size-4" />
                    <div className="text-xs font-medium">{o.title}</div>
                    <div className="text-[10px] text-muted-foreground line-clamp-2">
                      {o.desc}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {step === 2 && (
          <div data-testid="concierge-step-2" className="space-y-4">
            <div>
              <Label>Region</Label>
              <Select value={state.region_id || ""}
                          onValueChange={(v) => setField("region_id", v)}>
                <SelectTrigger data-testid="concierge-region-select">
                  <SelectValue placeholder="Choose a region" />
                </SelectTrigger>
                <SelectContent>
                  {regions.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Daily budget (EUR)</Label>
              <Input
                type="number" min="10" max="500" step="10"
                value={state.daily_budget_eur}
                onChange={(e) => setField("daily_budget_eur",
                    Number(e.target.value))}
                data-testid="concierge-budget-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                We recommend $50/day for your first campaign so you can
                learn what works before scaling up.
              </p>
            </div>
          </div>
        )}
        {step === 3 && (
          <div data-testid="concierge-step-3" className="space-y-3">
            {briefLoading && (
              <div className="text-sm text-muted-foreground"
                     data-testid="concierge-brief-loading">
                KI generiert deinen Kampagnen-Vorschlag…
              </div>
            )}
            {briefError && !briefLoading && (
              <div className="text-sm text-destructive"
                     data-testid="concierge-brief-error">
                Fehler beim Laden: {briefError}
              </div>
            )}
            {brief && !briefLoading && (
              <>
                {brief.source === "template_fallback" && (
                  <TemplateFallbackBanner
                    reason={brief.fallback_reason}
                    testId="concierge-fallback-banner"
                  />
                )}
                <div className="border rounded-lg p-3 space-y-2 bg-muted/20"
                       data-testid="concierge-brief-card">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    Strategie-Zusammenfassung
                  </div>
                  <div className="text-sm"
                         data-testid="concierge-brief-strategy">
                    {brief.strategy_summary || brief.goal_summary
                      || `Kampagne vom Typ ${brief.campaign_type} auf ${(brief.platforms || []).join(", ")}.`}
                  </div>
                  {Array.isArray(brief.platforms) && brief.platforms.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1"
                           data-testid="concierge-brief-platforms">
                      {brief.platforms.map((p) => (
                        <span key={p}
                                className="text-[10px] rounded-full border px-2 py-0.5 bg-background text-muted-foreground">
                          {p}
                        </span>
                      ))}
                    </div>
                  )}
                  {Array.isArray(brief.funnel_steps) && brief.funnel_steps.length > 0 && (
                    <ol className="text-xs list-decimal pl-4 pt-1"
                          data-testid="concierge-brief-funnel">
                      {brief.funnel_steps.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ol>
                  )}
                  {brief.offer && (
                    <div className="text-xs pt-1 space-y-0.5"
                           data-testid="concierge-brief-offer">
                      <div><strong>Headline:</strong> {brief.offer.headline}</div>
                      <div><strong>Nutzen:</strong> {brief.offer.value_prop}</div>
                      <div><strong>CTA:</strong> {brief.offer.cta}</div>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-muted-foreground"
                         data-testid="concierge-brief-source">
                    Quelle: <strong>{brief.source}</strong>
                    {typeof brief.regenerations_remaining === "number" && (
                      <> · Neu generieren: {brief.regenerations_remaining}/3</>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={fetchBrief}
                    disabled={briefLoading
                      || brief.regenerations_remaining === 0}
                    data-testid="concierge-brief-regenerate"
                    title={brief.regenerations_remaining === 0
                      ? "Maximal 3 Neugenerierungen pro Sitzung"
                      : "Neu generieren"}
                  >
                    Neu generieren
                  </Button>
                </div>

                {/* Wave 1 — Marketing Plan (AARRR) card. */}
                <div className="pt-2 border-t"
                        data-testid="concierge-marketing-plan-card">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-medium text-muted-foreground">
                      Marketing-Plan (AARRR) — via <code>marketing-plan</code>{" "}
                      skill
                    </div>
                    <Button size="sm" variant="outline"
                                onClick={fetchMarketingPlan}
                                disabled={planLoading}
                                data-testid="concierge-marketing-plan-generate">
                      {planLoading
                        ? <Loader2 className="size-3 mr-1 animate-spin" />
                        : <Sparkles className="size-3 mr-1" />}
                      {plan ? "Neu generieren" : "AARRR-Plan generieren"}
                    </Button>
                  </div>
                  {planError && (
                    <div className="text-sm text-destructive mt-2"
                            data-testid="concierge-marketing-plan-error">
                      Fehler: {planError}
                    </div>
                  )}
                  {plan && (
                    <div className="mt-3 space-y-2">
                      {plan.source === "template_fallback" && (
                        <TemplateFallbackBanner
                          reason={plan.fallback_reason}
                          testId="concierge-marketing-plan-fallback-banner"
                        />
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-2"
                              data-testid="concierge-marketing-plan-stages">
                        {["acquisition", "activation", "retention",
                            "referral", "revenue"].map((k) => {
                          const stage = (plan.stages || {})[k] || {};
                          return (
                            <div key={k}
                                    className="border rounded-md p-2 bg-muted/30"
                                    data-testid={`concierge-plan-stage-${k}`}>
                              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                {k}
                              </div>
                              <div className="text-xs font-medium mt-1">
                                {stage.objective || "—"}
                              </div>
                              {Array.isArray(stage.tactics)
                                  && stage.tactics.length > 0 && (
                                <ul className="text-[10px] list-disc pl-3 pt-1 text-muted-foreground space-y-0.5">
                                  {stage.tactics.slice(0, 3).map((tct, i) => (
                                    <li key={i}>{tct}</li>
                                  ))}
                                </ul>
                              )}
                              {typeof stage.budget_share_pct === "number" && (
                                <div className="text-[10px] text-muted-foreground mt-1">
                                  Budget: {stage.budget_share_pct}%
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {plan.north_star_metric && (
                        <div className="text-xs text-muted-foreground pt-1"
                                data-testid="concierge-plan-northstar">
                          North star: <strong>{plan.north_star_metric}</strong>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <div className="flex justify-between">
          <Button variant="ghost" onClick={() => setStep((s) => Math.max(1, s - 1))}
                     disabled={step === 1 || busy}
                     data-testid="concierge-back">
            <ChevronLeft className="size-4 mr-1" /> Back
          </Button>
          {step < 3 ? (
            <Button
              onClick={() => setStep((s) => Math.min(3, s + 1))}
              disabled={busy
                || (step === 1 && !state.campaign_type)
                || (step === 2 && !state.region_id)}
              data-testid="concierge-next"
            >
              Next <ChevronRight className="size-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={submit}
              disabled={busy}
              data-testid="concierge-submit"
            >
              {busy && <Loader2 className="size-4 mr-1 animate-spin" />}
              Create campaign
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
